// VideoCapturePage - Screen recording tool (local save / Google Drive only)
// Records screen + system audio + microphone, saves as MP4
import { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { useAuth } from "@/lib/auth-context";
import { WorkspaceLayout } from "@/components/layout/WorkspaceLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Clapperboard,
  Square,
  Circle,
  Loader2,
  Monitor,
  HardDrive,
  Cloud,
  Mic,
  MicOff,
  X,
  CheckCircle2,
} from "lucide-react";
import { toast } from "sonner";

export default function VideoCapturePage() {
  const { workspaceId } = useParams();
  const { canManage } = useAuth();

  // Recording state
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [includeMic, setIncludeMic] = useState(true);
  const [storageDestination, setStorageDestination] = useState(null); // 'local' | 'gdrive'
  const [showStorageStep, setShowStorageStep] = useState(false);
  const [pendingStream, setPendingStream] = useState(null);
  const [lastSaved, setLastSaved] = useState(null);

  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const streamRef = useRef(null);
  const displayStreamRef = useRef(null);
  const micStreamRef = useRef(null);

  // ─── Step 1: Capture microphone FIRST, then screen share ───
  const startScreenShare = async () => {
    try {
      // 1) Capture microphone FIRST (always works, any share mode)
      let micStream = null;
      if (includeMic) {
        try {
          micStream = await navigator.mediaDevices.getUserMedia({
            audio: {
              echoCancellation: true,
              noiseSuppression: true,
              autoGainControl: true,
              sampleRate: 48000,
            },
          });
        } catch {
          toast.info("Microphone non disponible");
        }
      }

      // 2) Then screen share (video only — no audio request to avoid 
      //    the "audio only with tab" limitation in Chrome)
      const displayStream = await navigator.mediaDevices.getDisplayMedia({
        video: {
          width: { ideal: 1920 },
          height: { ideal: 1080 },
          frameRate: { ideal: 30 },
        },
        audio: false, // Don't request system audio (Chrome blocks it for full screen)
      });

      // Store mic stream for later
      micStreamRef.current = micStream;
      setPendingStream(displayStream);
      setShowStorageStep(true);

      // If user cancels screen share while choosing storage
      displayStream.getVideoTracks()[0].onended = () => {
        if (!mediaRecorderRef.current) {
          setPendingStream(null);
          setShowStorageStep(false);
          if (micStream) micStream.getTracks().forEach((t) => t.stop());
          micStreamRef.current = null;
          toast.info("Partage d'écran annulé");
        }
      };
    } catch (err) {
      if (err.name !== "NotAllowedError") {
        toast.error("Impossible de démarrer le partage d'écran");
      }
    }
  };

  // ─── Step 2: Choose storage, then start recording ───
  const proceedWithRecording = async (destination) => {
    setShowStorageStep(false);
    setStorageDestination(destination);
    const displayStream = pendingStream;
    if (!displayStream) return;
    displayStreamRef.current = displayStream;

    try {
      const videoTracks = displayStream.getVideoTracks();
      const micStream = micStreamRef.current;

      // Build combined stream: video + mic audio tracks directly (no AudioContext)
      const combinedTracks = [...videoTracks];

      if (micStream && micStream.getAudioTracks().length > 0) {
        // Add mic audio tracks directly to the stream
        micStream.getAudioTracks().forEach((track) => {
          combinedTracks.push(track);
        });
        toast.success("🎙️ Microphone détecté et activé !");
      } else {
        toast.warning("⚠️ Aucun micro détecté — la vidéo sera sans son");
      }

      const combinedStream = new MediaStream(combinedTracks);

      // Log tracks for debugging
      console.log("Combined stream tracks:", combinedStream.getTracks().map(t => `${t.kind}: ${t.label} (${t.readyState})`));

      streamRef.current = combinedStream;
      chunksRef.current = [];

      // Use the best supported format with audio codec
      let mimeType = "video/webm";
      const candidates = [
        "video/webm;codecs=vp8,opus",
        "video/webm;codecs=vp9,opus",
        "video/webm;codecs=h264,opus",
        "video/webm",
      ];
      for (const candidate of candidates) {
        if (MediaRecorder.isTypeSupported(candidate)) {
          mimeType = candidate;
          break;
        }
      }

      console.log("Using mimeType:", mimeType);

      const mediaRecorder = new MediaRecorder(combinedStream, {
        mimeType,
        videoBitsPerSecond: 5000000,
        audioBitsPerSecond: 128000,
      });

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: "video/webm" });
        clearInterval(timerRef.current);
        setIsRecording(false);

        // Auto-save based on chosen destination
        autoSave(blob, destination);

        // Cleanup
        cleanupStreams();
      };

      // Auto-stop if user stops sharing
      displayStream.getVideoTracks()[0].onended = () => {
        if (mediaRecorderRef.current?.state === "recording") {
          mediaRecorderRef.current.stop();
        }
      };

      mediaRecorder.start(1000);
      mediaRecorderRef.current = mediaRecorder;
      setPendingStream(null);
      setIsRecording(true);
      setRecordingTime(0);
      setLastSaved(null);

      timerRef.current = setInterval(() => {
        setRecordingTime((prev) => prev + 1);
      }, 1000);

      const audioCount = combinedStream.getAudioTracks().length;
      toast.success(`Enregistrement démarré (${audioCount} piste${audioCount > 1 ? 's' : ''} audio)`);
    } catch (err) {
      console.error("Recording error:", err);
      toast.error("Erreur lors du démarrage");
      cleanupStreams();
    }
  };

  // ─── Auto-save based on destination ───
  const autoSave = async (blob, destination) => {
    const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, "-");
    const filename = `capture_${timestamp}.webm`;
    const sizeMB = (blob.size / (1024 * 1024)).toFixed(1);

    if (destination === "local") {
      // Use File System Access API to let user choose save location
      try {
        if (window.showSaveFilePicker) {
          const handle = await window.showSaveFilePicker({
            suggestedName: filename,
            types: [
              {
                description: "Fichier vidéo WebM",
                accept: { "video/webm": [".webm"] },
              },
            ],
          });
          const writable = await handle.createWritable();
          await writable.write(blob);
          await writable.close();
          setLastSaved({
            type: "local",
            filename: handle.name,
            size: sizeMB,
            message: `Vidéo "${handle.name}" (${sizeMB} MB) sauvegardée avec succès !`,
          });
          toast.success(`Vidéo sauvegardée : ${handle.name}`);
          return;
        }
      } catch (err) {
        if (err.name === "AbortError") {
          // User cancelled the save dialog
          toast.info("Sauvegarde annulée");
          return;
        }
        // Fall back to auto-download
      }

      // Fallback: auto-download
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      setLastSaved({
        type: "local",
        filename,
        size: sizeMB,
        message: `Vidéo "${filename}" (${sizeMB} MB) téléchargée dans votre dossier Téléchargements.`,
      });
      toast.success(`Vidéo téléchargée : ${filename}`);

    } else if (destination === "gdrive") {
      // Step 1: Save file locally first so user has the file
      let savedName = filename;
      try {
        if (window.showSaveFilePicker) {
          const handle = await window.showSaveFilePicker({
            suggestedName: filename,
            types: [
              {
                description: "Fichier vidéo WebM",
                accept: { "video/webm": [".webm"] },
              },
            ],
          });
          const writable = await handle.createWritable();
          await writable.write(blob);
          await writable.close();
          savedName = handle.name;
        } else {
          const url = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = filename;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
        }
      } catch (err) {
        if (err.name === "AbortError") {
          toast.info("Sauvegarde annulée");
          return;
        }
      }

      // Step 2: Open Google Drive upload page (user can choose folder)
      setTimeout(() => {
        window.open("https://drive.google.com/drive/my-drive?action=upload", "_blank");
      }, 800);

      setLastSaved({
        type: "gdrive",
        filename: savedName,
        size: sizeMB,
        message: `Vidéo "${savedName}" (${sizeMB} MB) sauvegardée. Google Drive s'ouvre — cliquez "Nouveau" → "Importer un fichier" et sélectionnez le fichier.`,
      });
      toast.success("Vidéo sauvegardée ! Google Drive ouvert pour l'import.");
    }
  };

  // ─── Cleanup ───
  const cleanupStreams = () => {
    if (displayStreamRef.current) {
      displayStreamRef.current.getTracks().forEach((t) => t.stop());
      displayStreamRef.current = null;
    }
    if (micStreamRef.current) {
      micStreamRef.current.getTracks().forEach((t) => t.stop());
      micStreamRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    mediaRecorderRef.current = null;
  };

  const cancelStorageStep = () => {
    if (pendingStream) {
      pendingStream.getTracks().forEach((t) => t.stop());
      setPendingStream(null);
    }
    setShowStorageStep(false);
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current?.state === "recording") {
      mediaRecorderRef.current.stop();
    }
  };

  const formatDuration = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearInterval(timerRef.current);
      cleanupStreams();
    };
  }, []);

  return (
    <WorkspaceLayout>
      <div className="page-container animate-fade-in">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-semibold tracking-tight flex items-center gap-3">
            <Clapperboard className="h-8 w-8 text-primary" />
            Capture Vidéo
          </h1>
          <p className="mt-1 text-muted-foreground">
            Enregistrez votre écran avec le son et sauvegardez la vidéo
          </p>
        </div>

        {/* Recording Controls (Trainer/Admin only) */}
        {canManage ? (
          <>
            <Card
              className="mt-6"
              style={{
                borderLeft: isRecording
                  ? "4px solid #ef4444"
                  : "4px solid hsl(var(--primary))",
              }}
            >
              <CardContent className="pt-6">
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <div className="flex items-center gap-4">
                    <div
                      className="flex h-14 w-14 items-center justify-center rounded-xl"
                      style={{
                        background: isRecording
                          ? "linear-gradient(135deg, #ef4444, #dc2626)"
                          : "linear-gradient(135deg, hsl(var(--primary)), hsl(var(--primary) / 0.8))",
                      }}
                    >
                      {isRecording ? (
                        <Circle
                          className="h-6 w-6 text-white"
                          style={{ animation: "pulse 1s infinite" }}
                        />
                      ) : (
                        <Monitor className="h-6 w-6 text-white" />
                      )}
                    </div>
                    <div>
                      <h3 className="font-semibold">
                        {isRecording
                          ? "Enregistrement en cours..."
                          : "Capturer votre écran"}
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        {isRecording ? (
                          <>
                            Durée : <strong>{formatDuration(recordingTime)}</strong>
                            {storageDestination && (
                              <Badge variant="outline" className="ml-2 text-xs">
                                {storageDestination === "local" ? (
                                  <><HardDrive className="h-3 w-3 mr-1" /> Local</>
                                ) : (
                                  <><Cloud className="h-3 w-3 mr-1" /> Google Drive</>
                                )}
                              </Badge>
                            )}
                          </>
                        ) : (
                          "Écran + audio système + microphone → MP4"
                        )}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    {!isRecording && !showStorageStep && (
                      <>
                        <Button
                          variant="outline"
                          size="sm"
                          className="gap-1"
                          onClick={() => setIncludeMic(!includeMic)}
                        >
                          {includeMic ? (
                            <Mic className="h-4 w-4 text-green-600" />
                          ) : (
                            <MicOff className="h-4 w-4 text-red-500" />
                          )}
                          {includeMic ? "Micro ON" : "Micro OFF"}
                        </Button>
                        <Button
                          onClick={startScreenShare}
                          className="gap-2"
                          style={{
                            background:
                              "linear-gradient(135deg, #ef4444, #dc2626)",
                          }}
                        >
                          <Circle className="h-4 w-4" /> Démarrer
                        </Button>
                      </>
                    )}
                    {isRecording && (
                      <Button
                        onClick={stopRecording}
                        variant="destructive"
                        className="gap-2"
                        size="lg"
                      >
                        <Square className="h-4 w-4" /> Arrêter l'enregistrement
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Storage Destination Step */}
            {showStorageStep && (
              <Card className="mt-4 border-blue-500/30">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <HardDrive className="h-5 w-5 text-blue-600" />
                    Où sauvegarder la vidéo après l'enregistrement ?
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid gap-4 sm:grid-cols-2">
                    {/* Local Storage */}
                    <button
                      type="button"
                      onClick={() => proceedWithRecording("local")}
                      className="flex items-center gap-4 p-5 rounded-xl bg-card transition-all hover:shadow-lg text-left group"
                      style={{
                        border: "2px solid hsl(var(--border))",
                      }}
                      onMouseOver={(e) =>
                        (e.currentTarget.style.borderColor =
                          "hsl(var(--primary))")
                      }
                      onMouseOut={(e) =>
                        (e.currentTarget.style.borderColor =
                          "hsl(var(--border))")
                      }
                    >
                      <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-blue-100 dark:bg-blue-900/30 group-hover:scale-110 transition-transform">
                        <HardDrive className="h-6 w-6 text-blue-600" />
                      </div>
                      <div>
                        <p className="font-semibold">Disque local</p>
                        <p className="text-xs text-muted-foreground">
                          Choisissez le dossier où sauvegarder le fichier vidéo
                        </p>
                      </div>
                    </button>

                    {/* Google Drive */}
                    <button
                      type="button"
                      onClick={() => proceedWithRecording("gdrive")}
                      className="flex items-center gap-4 p-5 rounded-xl bg-card transition-all hover:shadow-lg text-left group"
                      style={{
                        border: "2px solid hsl(var(--border))",
                      }}
                      onMouseOver={(e) =>
                        (e.currentTarget.style.borderColor = "#22c55e")
                      }
                      onMouseOut={(e) =>
                        (e.currentTarget.style.borderColor =
                          "hsl(var(--border))")
                      }
                    >
                      <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-green-100 dark:bg-green-900/30 group-hover:scale-110 transition-transform">
                        <Cloud className="h-6 w-6 text-green-600" />
                      </div>
                      <div>
                        <p className="font-semibold">Google Drive</p>
                        <p className="text-xs text-muted-foreground">
                          Sauvegardez le fichier, puis importez-le dans le dossier Google Drive de votre choix
                        </p>
                      </div>
                    </button>
                  </div>

                  <div className="mt-4">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={cancelStorageStep}
                      className="gap-1 text-muted-foreground"
                    >
                      <X className="h-4 w-4" /> Annuler
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Success Message */}
            {lastSaved && (
              <Card className="mt-4 border-green-500/30 bg-green-50 dark:bg-green-950/20">
                <CardContent className="pt-6">
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="h-6 w-6 text-green-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-semibold text-green-700 dark:text-green-400">
                        Vidéo sauvegardée avec succès !
                      </p>
                      <p className="text-sm text-muted-foreground mt-1">
                        {lastSaved.message}
                      </p>
                      <div className="flex items-center gap-3 mt-2">
                        <Badge variant="outline" className="text-xs">
                          {lastSaved.type === "local" ? (
                            <><HardDrive className="h-3 w-3 mr-1" /> Local</>
                          ) : (
                            <><Cloud className="h-3 w-3 mr-1" /> Google Drive</>
                          )}
                        </Badge>
                        <span className="text-xs text-muted-foreground">
                          {lastSaved.size} MB
                        </span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Tips */}
            {!isRecording && !showStorageStep && (
              <Card className="mt-6 border-dashed">
                <CardContent className="pt-6">
                  <h3 className="font-semibold text-sm mb-3">
                    💡 Conseils pour un bon enregistrement
                  </h3>
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    <li className="flex items-start gap-2">
                      <span className="text-primary font-bold">1.</span>
                      <span>
                        Activez <strong>Micro ON</strong> pour que votre voix soit enregistrée avec la vidéo
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary font-bold">2.</span>
                      <span>
                        Vous pouvez partager <strong>tout l'écran</strong>, une <strong>fenêtre</strong> ou un <strong>onglet</strong>
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary font-bold">3.</span>
                      <span>
                        Choisissez le <strong>stockage</strong> (local ou Google Drive) avant le début de l'enregistrement
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-primary font-bold">4.</span>
                      <span>
                        À l'arrêt, la vidéo <strong>WebM</strong> est automatiquement sauvegardée — vous choisissez le dossier de destination
                      </span>
                    </li>
                  </ul>
                </CardContent>
              </Card>
            )}
          </>
        ) : (
          <Card className="mt-6 border-dashed">
            <CardContent className="flex flex-col items-center justify-center py-16">
              <Clapperboard className="h-12 w-12 text-muted-foreground" />
              <h3 className="mt-4 font-semibold">Fonctionnalité réservée aux formateurs</h3>
              <p className="mt-2 text-sm text-muted-foreground text-center">
                Seuls les formateurs et administrateurs peuvent enregistrer des vidéos.
              </p>
            </CardContent>
          </Card>
        )}

        <style>{`
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.3; }
          }
        `}</style>
      </div>
    </WorkspaceLayout>
  );
}
